import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { toolTipPointFormatterGenerator, labelFormatterGenerator } from 'helpers/charts';
import { reduceLayerTwoData } from 'helpers/data';
import RenderingTile from 'components/molecules/RenderingTile';
import TwoAxisBarWithLineChart from 'components/molecules/charts/TwoAxisBarWithLineChart';
import TwoAxisBarWithScatterChart from 'components/molecules/charts/TwoAxisBarWithScatterChart';
import SocialGroupedBarChart from 'components/molecules/charts/SocialGroupedBarChart';
import SocialAdSetOverviewPivotTable from 'components/molecules/tables/SocialAdSetOverviewPivotTable';
import SocialConversionsChart from 'components/molecules/charts/SocialConversionsChart';
import { LAYERTHREE, LAYERTWO } from 'constants/layers';
import { getActivitiesSettings } from 'helpers/objectiveSettings';

// LayerTwo Charts & Tables
import LayerTwoSummaryTable from 'components/molecules/tables/LayerTwoSummaryTable';
import SocialActionOverviewTable from 'components/molecules/tables/SocialActionOverviewTable';

class LinkClicksExpandModule extends Component {
    render() {
        const {
            backfillBuckets,
            xAxisLabel,
            layer = LAYERTHREE,
            layerTwo,
            showConversionRevenue,
            entityMeta,
            entityObjective,
            entityObjectiveLabelCode
        } = this.props;
        const {
            conversionTermSingular,
        } = getActivitiesSettings(entityObjective, entityObjectiveLabelCode);
        const stub = `l${layer}-social`;
        const { ads, adsets, campaigns, timeData, actions } = this.props.module;
        const hasAdSetData = adsets.hasData;
        const { data } = timeData;
        const hasTimeData = get(timeData, 'hasData', null);
        const l2Data = get(layerTwo, 'social', {});
        const hasLayerTwoSummaryData = get(l2Data, 'summary.hasData', null);
        const aliasDescriptor = get(this.props, 'entityMeta.childAliasNames.pick', 'LOCATION');
        const userId = get(this.props, 'currentUser.id', null);
        const entityId = get(this.props, 'entityId', null);

        let childSummaryTableData = [];
        if (hasLayerTwoSummaryData) {
            const reducedData = reduceLayerTwoData(l2Data.summary);
            childSummaryTableData = reducedData.childSummaryTableData;
        }
        const rankedClicks = hasAdSetData ? adsets.rankings.clicks.reduce((prev, adset) => {
            const adSetName = adsets.adSetIdMap[adset.name].name;
            const statistics = adsets[adset.name].statistics;
            prev.data.push(statistics);
            prev.categories.push(adSetName);

            return prev;
        }, { categories: [], data: [] }) : {};

        const hasAdsData = get(ads, 'hasData', null);
        let adsData = null;
        if (hasAdsData) {
            const scopeKey = ads.categories.config.selector;
            adsData = ads.rankings.link_click_rate.reduce((prev, adData) => {
                const key = adData.name;
                const dataArray = prev.ads[key][prev.scopeKey];

                prev.graph[key] = dataArray;
                prev.sortedKeys.push(key);

                return prev;
            }, { graph: {}, sortedKeys: [], scopeKey, ads });
        }

        const objectives = get(entityMeta, 'social.objectives', []);
        const showLayerTwoSummaryTable = !!(hasLayerTwoSummaryData && (layer === LAYERTWO) && (objectives.length > 1));
        const showCampaignSummaryTable = !!(hasAdSetData && layer === LAYERTHREE);

        return (
            <React.Fragment>
                {/* Link Clicks VS. CPC */}
                {hasAdSetData ? (
                    <TwoAxisBarWithScatterChart
                        title="LINK CLICKS VS. COST / LINK CLICK"
                        exportName={`${stub}-link-clicks-link-clicks-vs-cost-per-click`}
                        data={rankedClicks.data}
                        yBar="link_clicks"
                        yBarTitle="Link Clicks"
                        scatter="cost_per_link_click"
                        scatterTitle="Cost / Link Click"
                        toolTipOptions={{
                            pointFormatter: toolTipPointFormatterGenerator
                        }}
                        yScatterOptions={{
                            labels: {
                                formatter: labelFormatterGenerator('priceWithoutSpace')
                            }
                        }}
                        yScatterUserOptions={{ formatByType: 'priceWithoutSpace' }}
                        animateDuration={2000}
                        categories={rankedClicks.categories} />
                ) : (
                    <RenderingTile height='400px' show={hasAdSetData} />
                )}

                {/* Link Clicks VS. Frequency */}

                {/* Impressions VS. CTR */}
                {hasTimeData ? (
                    <TwoAxisBarWithLineChart
                        title="IMPRESSIONS VS. LINK CLICK RATE"
                        exportName={`${stub}-link-clicks-impressions-vs-link-click-rate`}
                        data={data}
                        yBar="impressions"
                        yBarOptions={{ formatByType: 'number' }}
                        yLine="link_click_rate"
                        yLineTitle="Link Click Rate"
                        yLineOptions={{ formatByType: 'percentage' }}
                        toolTipOptions={{
                            pointFormatter: toolTipPointFormatterGenerator
                        }}
                        yAxisLineOptions={{
                            labels: {
                                formatter: labelFormatterGenerator('percentage')
                            }
                        }}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        animateDuration={2000}
                        xAxisType="datetime" />
                ) : (
                    <RenderingTile height='400px' show={hasTimeData} />
                )}

                {/* CTR By Ad */}
                {hasAdsData ? (
                    <SocialGroupedBarChart
                        animateDuration={2000}
                        data={adsData.graph}
                        exportName={`${stub}-link-clicks-link-clicks-rate-by-ad`}
                        keys={adsData.sortedKeys}
                        idMap={ads.adIdMap}
                        title="LINK CLICK RATE BY AD"
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        xAxisType="datetime"
                        y="link_click_rate"
                        yFormatByType='percentageNoPrecision' />
                ) : (
                    <RenderingTile height='400px' show={hasAdsData} />
                )}
                <SocialConversionsChart actions={actions} exportNamePrefix={`${stub}-link-clicks`} />

                {showLayerTwoSummaryTable ? (
                    <LayerTwoSummaryTable
                        title={`${aliasDescriptor} OVERVIEW`}
                        channel={'social'}
                        aliasDescriptor={aliasDescriptor}
                        showConversionRevenue={showConversionRevenue}
                        data={childSummaryTableData}
                        exportName='l2-social-link-clicks-table-performance'
                        accessors={['impressions', 'link_clicks', 'cost_per_link_click', 'link_click_rate', 'clicks', 'cpc', 'ctr', 'conversions', 'engagements', 'spend']}
                        cacheKey={`l2-social-link-clicks-table-performance-${userId}-${entityId}`}
                    />
                ) : (
                    <RenderingTile height='400px' show={showLayerTwoSummaryTable} />
                )}

                {/* Campaign Overview */}
                {showCampaignSummaryTable ? (
                    <SocialAdSetOverviewPivotTable
                        title="Campaign Overview"
                        exportName='l3-social-link-clicks-summary-adsets-overview'
                        adSets={adsets}
                        showConversionRevenue={showConversionRevenue}
                        accessors={['impressions', 'link_clicks', 'cost_per_link_click', 'link_click_rate', 'clicks', 'cpc', 'ctr', 'conversions', 'engagements', 'budget']}
                    />
                ) : (
                    <RenderingTile height='400px' show={showCampaignSummaryTable} />
                )}

                <SocialActionOverviewTable
                    exportName={`${stub}-link-clicks-action-overview`}
                    actions={actions}
                    campaigns={campaigns}
                    conversionTermSingular={conversionTermSingular}
                    cacheKey={`${stub}-link-clicks-action-overview-${userId}-${entityId}`}
                />
            </React.Fragment>
        );
    }
}

LinkClicksExpandModule.propTypes = {
    module: PropTypes.shape({
        actions: PropTypes.object,
        ads: PropTypes.object,
        adsets: PropTypes.object,
        campaigns: PropTypes.object,
        timeData: PropTypes.object
    }).isRequired
};

export default LinkClicksExpandModule;



// WEBPACK FOOTER //
// ./src/components/organisms/social/LinkClicksExpandModule.js